import React from 'react';
import {
  CreateButton,
  EditButton,
  Datagrid,
  Title,
  ListBase,
  TextField,
  TopToolbar,
  ExportButton,
  Pagination,
  SimpleListConfigurable,
  FunctionField,
  TextInput,
  BooleanInput,
  useNotify,
  useRedirect,
  usePermissions,
} from 'react-admin';
import { Stack, Tooltip, useMediaQuery } from '@mui/material';
import styled from 'styled-components';
import { EmptyFieldWithRecord } from '../../Shared/EmptyFieldWithRecord';
import { CustomBooleanField } from '../../Shared/CustomBooleanField';
import { FilterForm } from '../../Shared/FilterForm';
import { WithLoading } from '../../Shared/Loading';
import { checkPermission } from '../../Admin/Admin.utilities';
import { AdminAccessRightListEnum } from '../../Shared/Admin.types';
import {
  formatTimeForDisplay,
  getChannelLabel,
} from '../../Shared/ScheduledMessage.types';

const StyledTextInput = styled(TextInput)`
  .MuiInputBase-input:focus ~ div .MuiButtonBase-root {
    color: #ff5722;
  }
`;

const filters = (fullWidth: boolean): React.ReactNode[] => {
  return [
    <StyledTextInput
      key="filterSearchInput"
      label="Search"
      source="q"
      alwaysOn
      fullWidth={fullWidth}
      resettable
    />,
    <BooleanInput key="filterActive" source="active" label="Active only?" />,
  ];
};

const ListActions: React.FC = () => {
  const isSmallDevice = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );
  const { permissions } = usePermissions();

  return (
    <Stack
      direction={isSmallDevice ? 'column' : 'row'}
      marginTop={2}
      marginBottom={2}
    >
      <FilterForm filters={filters(isSmallDevice)} fullWidth={isSmallDevice} />
      <TopToolbar
        sx={{
          backgroundColor: 'transparent!important',
          width: '100%',
        }}
      >
        {checkPermission(permissions, [
          AdminAccessRightListEnum.CreateScheduledMessages,
        ]) && <CreateButton />}
        <ExportButton />
      </TopToolbar>
    </Stack>
  );
};

const ScheduledMessageList: React.FC = () => {
  const isSmallDevice = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );
  const notify = useNotify();
  const redirect = useRedirect();

  const onError = (error: any): void => {
    console.error('Error loading list:', error);
    notify(`Could not load list: ${error.message}`, { type: 'warning' });
    redirect('/dashboard');
  };

  return (
    <ListBase
      debounce={500}
      perPage={10}
      queryOptions={{ onError }}
      sort={{ field: 'id', order: 'DESC' }}
    >
      <Title title="Galactic Entertainment" />
      <ListActions />
      <WithLoading>
        <>
          {isSmallDevice ? (
            <SimpleListConfigurable
              primaryText={(record) => formatTimeForDisplay(record.time)}
              secondaryText={(record) => record.content}
              linkType="edit"
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick="edit"
              sx={{
                '& .column-content': {
                  maxWidth: '420px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .column-actions': {
                  textAlign: 'right',
                  '& .MuiStack-root': {
                    justifyContent: 'flex-end',
                  },
                  '& .MuiButtonBase-root': {
                    minWidth: 'max-content',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: '0',
                  },
                },
              }}
            >
              <EmptyFieldWithRecord source="id" label="ID">
                <TextField source="id" label="ID" />
              </EmptyFieldWithRecord>
              <FunctionField
                source="time"
                label="Send Time"
                render={(record: any) => formatTimeForDisplay(record?.time)}
              />
              <FunctionField
                source="channelId"
                label="Channel"
                render={(record: any) => getChannelLabel(record?.channelId)}
              />
              <FunctionField
                source="content"
                label="Message"
                className="column-content"
                render={(record: any) => record?.content}
              />
              <CustomBooleanField source="active" label="Active?" />
              <FunctionField
                source="actions"
                label="Actions"
                className="column-actions"
                render={(record: any) => (
                  <Stack direction="row" justifyContent="flex-end">
                    <Tooltip title="Edit" arrow>
                      <span>
                        <EditButton record={record} label="" />
                      </span>
                    </Tooltip>
                  </Stack>
                )}
              />
            </Datagrid>
          )}
          <Pagination />
        </>
      </WithLoading>
    </ListBase>
  );
};

export default ScheduledMessageList;
